import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Include } from '../lib/canvasContent';
import type { ScreenRotation, ScreenSchedule } from '../services/api';
import { newId } from './settings';

// This screen's identity for Settings → Screens: a random id kept in this browser, and –
// per signed-in account – what the account has chosen for it: one saved layout (or the main
// setup), or a rotation through several, and its on/off schedule (`plans`, from check-ins in
// useScreenHeartbeat). `assignments` is what it shows right now – the layout, or the
// rotation's current item (hooks/useScreenPlan.ts). Both are remembered, so a screen starts
// on the right layout, and keeps to its schedule, before its first check-in.

/** What the account has chosen for this screen. */
export interface ScreenPlan {
  presetId: string | null;
  rotation: ScreenRotation | null;
  /** The schedule it follows; null = always on. */
  schedule: ScreenSchedule | null;
}

interface ScreenStore {
  deviceId: string;
  /** userId → saved layout id (null = main setup). */
  assignments: Record<string, string | null>;
  setAssignment: (userId: string, presetId: string | null) => void;
  /** userId → this screen's layout/rotation and schedule. */
  plans: Record<string, ScreenPlan>;
  setPlan: (userId: string, plan: Partial<ScreenPlan>) => void;
  /** Switched off by its schedule right now (not persisted), for check-ins. */
  off: boolean;
  setOff: (off: boolean) => void;
  /** The shared canvas this screen is showing right now (not persisted), for notices in Settings. */
  canvas: { id: string; name: string; ownerEmail: string; edit: boolean; mine: boolean; include: Include } | null;
  setCanvas: (c: ScreenStore['canvas']) => void;
}

const freshId = () => newId().replace(/[^A-Za-z0-9-]/g, '').slice(0, 40) || `screen-${Date.now().toString(36)}`;

export const useScreen = create<ScreenStore>()(
  persist(
    (set) => ({
      deviceId: freshId(),
      assignments: {},
      setAssignment: (userId, presetId) =>
        set((s) => (s.assignments[userId] === presetId ? s : { assignments: { ...s.assignments, [userId]: presetId } })),
      plans: {},
      setPlan: (userId, plan) =>
        set((s) => ({ plans: { ...s.plans, [userId]: { presetId: null, rotation: null, schedule: null, ...s.plans[userId], ...plan } } })),
      off: false,
      setOff: (off) => set((s) => (s.off === off ? s : { off })),
      canvas: null,
      setCanvas: (canvas) => set({ canvas }),
    }),
    { name: 'kcl-canvas-screen', version: 1, partialize: (s) => ({ deviceId: s.deviceId, assignments: s.assignments, plans: s.plans }) },
  ),
);

/** The saved layout this screen shows for `userId` (null = main setup). */
export const assignmentFor = (userId: string | null | undefined): string | null =>
  (userId && useScreen.getState().assignments[userId]) || null;

/**
 * When this screen shows someone else's shared canvas that includes their OneDrive, Outlook,
 * To Do or Planner content, the canvas id to fetch it through (with the owner's connection); else null.
 */
export function useSharedSource(what: 'onedrive' | 'outlook' | 'todo' | 'planner' | 'powerbi'): string | null {
  return useScreen((s) => (s.canvas && !s.canvas.mine && s.canvas.include[what] ? s.canvas.id : null));
}
